import { type FC, useState } from "react";

import {
  Box,
  Column,
  ExperimentIcon,
  IconButton,
  Row,
  Skeleton,
  SkeletonBox,
  Text,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@hightouch/htui";
import orderBy from "lodash/orderBy";

import { Editor } from "src/components/editor";
import { EditorView } from "@codemirror/view";

import { convertType, NEW_ICON_MAP } from "src/utils/destinations";
import {
  useFormkitContext,
  useFormkitDataContext,
} from "src/formkit/components/formkit-context";
import type { FormProps, JsonColumnProps } from "src/formkit/components/types";
import {
  getTemplateColumnItems,
  insertTemplateFunction,
  insertTemplateVariable,
  type TemplateValueKind,
} from "./template-input-utils";
import { useTemplatePreview } from "./template-preview-context";

const getMetadataFields = ({ isRealtimeSync }: { isRealtimeSync: boolean }) => [
  // XXX these are defined provided as context in
  // `/packages/backend/sdk/lib/liquid-template/liquid.ts`
  { label: "Model ID", value: "context['model_id']" },
  { label: "Model name", value: "context['model_name']" },
  { label: "Sync ID", value: "context['sync_id']" },
  { label: "Current timestamp", value: "context['timestamp']" },
  ...(isRealtimeSync
    ? [
        // Note that these are actually in the `row` object at mapping time
        // but we want them to appear as metadata variables which is why
        // they are part of this list.
        {
          label: "Message ID",
          value: "row['messageId']",
        },
        {
          label: "Trigger Event Message ID",
          value: "row['triggerEvent.messageId']",
        },
        {
          label: "Trigger Event Timestamp",
          value: "row['triggerEvent.timestamp']",
        },
        {
          label: "Trigger Event OriginalTimestamp",
          value: "row['triggerEvent.originalTimestamp']",
        },
        {
          label: "Trigger Event SentAt",
          value: "row['triggerEvent.sentAt']",
        },
        {
          label: "Trigger Event ReceivedAt",
          value: "row['triggerEvent.receivedAt']",
        },
      ]
    : [
        { label: "Sync run ID", value: "context['sync_run_id']" },
        { label: "Operation", value: "context['operation']" },
      ]),
];

const rowSx = {
  alignItems: "center",
  borderBottom: "small",
  fontSize: 0,
  fontWeight: "semi",
  minHeight: "36px",
  px: 1,
  ":hover": { bg: "base.background" },
};

export const TemplateInput: FC<
  Readonly<FormProps & { jsonColumnProperties: JsonColumnProps }>
> = ({
  columnOptions,
  onChange,
  overwriteColumnsWithArrayProps,
  templates,
  value,
}) => {
  const { columns: formkitColumns, model } = useFormkitContext();
  const { loadingRows } = useFormkitDataContext();
  const templatePreview = useTemplatePreview();
  const isRealtimeSync = Boolean(model?.is_realtime_audience);

  const templateField = {
    to: value.to ?? "",
    template: value.template ?? "",
    applyTemplate: (newTemplate: string) =>
      onChange({ ...value, template: newTemplate }),
  };
  const columns = overwriteColumnsWithArrayProps
    ? (columnOptions ?? [])
    : formkitColumns;
  const [toggle, setToggle] = useState("data");

  const columnItems = getTemplateColumnItems(columns);
  const sortedTemplates = orderBy(templates, "name");

  const metadataFields = getMetadataFields({ isRealtimeSync });

  const getValueKind = (val: string): TemplateValueKind => {
    if (metadataFields.some((field) => field.value === val)) {
      return "metadata";
    }
    const matches = columnItems.filter((item) => item.value === val);
    if (matches.some((item) => item.kind === "trait")) {
      return "trait";
    }
    if (matches.some((item) => item.kind === "merged")) {
      return "merged";
    }
    return "row";
  };

  const insertVar = (val: string) => {
    onChange({
      ...value,
      template: insertTemplateVariable(value.template, val, getValueKind(val)),
    });
  };

  const insertFunction = (
    name: string,
    placeholders?: string[],
    inputValue?: string,
  ) => {
    onChange({
      ...value,
      template: insertTemplateFunction(value.template, {
        name,
        placeholders,
        inputValue,
      }),
    });
  };

  return (
    <Row flex="1" minHeight="0" mx={3} my={1}>
      <Box fontFamily="monospace" maxWidth="444px" mr={2} width="56.5%">
        <Editor
          minHeight="200px"
          language="liquid"
          // Wrap long Liquid lines instead of scrolling horizontally, matching
          // the plain <Textarea> this editor replaced.
          extensions={[EditorView.lineWrapping]}
          placeholder="Click on any variable/filter on the right to inject into Liquid template…"
          value={value.template ?? ""}
          onChange={(template) => onChange({ ...value, template })}
          actions={
            templatePreview ? (
              <Tooltip message="Test template">
                <IconButton
                  aria-label="Test template"
                  icon={ExperimentIcon}
                  size="sm"
                  variant="secondary"
                  onClick={() => templatePreview.openPreview(templateField)}
                />
              </Tooltip>
            ) : undefined
          }
        />
      </Box>
      <Column flex={1}>
        <Row alignItems="center" height="32px" p={2}>
          <ToggleButtonGroup onChange={setToggle} value={toggle}>
            <ToggleButton label="Columns" value="data" />
            <ToggleButton label="Metadata" value="metadata" />
            <ToggleButton label="Filters" value="functions" />
          </ToggleButtonGroup>
        </Row>
        <Column mt={2} overflowY="auto" overscrollBehaviorY="contain">
          {toggle === "data" && (
            <>
              {overwriteColumnsWithArrayProps && loadingRows ? (
                <ShimmerLoadingState />
              ) : (
                <DataList data={columnItems} hasIcon onClick={insertVar} />
              )}
            </>
          )}
          {toggle === "metadata" && (
            <DataList data={metadataFields} onClick={insertVar} />
          )}
          {toggle === "functions" && (
            <DataList data={sortedTemplates} onClick={insertFunction} />
          )}
        </Column>
      </Column>
    </Row>
  );
};

const DataList = ({ data, hasIcon = false, onClick }) => (
  <>
    {data.map(
      ({
        customType,
        description,
        inlineDescription,
        inputValue,
        label,
        name,
        placeholders,
        type,
        value,
      }) => (
        <Row
          key={label || name}
          _hover={{
            bg: "base.background",
          }}
          borderBottomWidth="hairline"
          borderColor="base.border"
          cursor="pointer"
          display="flex"
          flex="none"
          gap={2}
          p={2}
          onClick={() => {
            onClick(value || name, placeholders, inputValue);
          }}
        >
          {hasIcon && (
            <Box
              alignSelf="start"
              as={NEW_ICON_MAP[convertType(customType || type)]}
              boxSize={5}
              color="text.tertiary"
            />
          )}
          <Column maxWidth="266px">
            <Row gap={2}>
              <Text
                color="text.primary"
                fontWeight="medium"
                isTruncated
                size="md"
              >
                {label || name}
              </Text>
              {inlineDescription && (
                <Text color="text.secondary" isTruncated size="md">
                  {inlineDescription}
                </Text>
              )}
            </Row>
            {description && (
              <Box color="text.secondary" fontSize="sm">
                {description}
              </Box>
            )}
          </Column>
        </Row>
      ),
    )}
  </>
);

const ShimmerLoadingState = () => (
  <Skeleton isLoading={true}>
    {Array(4).map((index) => (
      <Row key={index} sx={rowSx}>
        <SkeletonBox borderRadius="sm" height="18px" width="100%" />
      </Row>
    ))}
  </Skeleton>
);
